import React, { useState } from "react";

const TYPE_STYLE = {
  annual:    "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  sick:      "text-red-400 bg-red-500/10 border-red-500/20",
  casual:    "text-purple-400 bg-purple-500/10 border-purple-500/20",
  unpaid:    "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  wfh:       "text-blue-400 bg-blue-500/10 border-blue-500/20",
  maternity: "text-pink-400 bg-pink-500/10 border-pink-500/20",
  paternity: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
};

export default function LeaveApprovalsQueue({ loading, items, onApprove, onReject }) {
  // Per-row comment state
  const [comments, setComments] = useState({});
  const setComment = (id, val) => setComments(c => ({ ...c, [id]: val }));

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl border border-[#00ff90]/20 bg-[#0d0d0d] animate-pulse" />
        ))}
      </div>
    );
  }

  if (!items?.length) {
    return <p className="text-white/30 text-sm py-4 text-center italic">No pending approvals.</p>;
  }

  return (
    <div className="space-y-3">
      {items.map(x => {
        const typeKey = (x.type || "").toLowerCase();
        const typeStyle = TYPE_STYLE[typeKey] || "text-white/50 bg-white/5 border-white/10";
        const isPending = x.status === "PENDING";

        return (
          <div key={x.id} className="rounded-xl border border-[#00ff90]/15 bg-[#0d0d0d] p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-sm font-medium text-white">{x.name}</span>
                  <span className="text-xs text-white/30">{x.role}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${typeStyle}`}>{x.type}</span>
                  {x.conflicts?.length > 0 && (
                    <span className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full">
                      ⚠ {x.conflicts.length} conflict{x.conflicts.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <p className="text-xs text-white/40">
                  {x.dateRange} · {x.days} day{x.days !== 1 ? "s" : ""}
                  {x.note && <> · <span className="italic">"{x.note}"</span></>}
                </p>
                {x.conflicts?.length > 0 && (
                  <p className="text-xs text-red-400/60 mt-0.5">Overlaps: {x.conflicts.join(", ")}</p>
                )}
              </div>

              {isPending ? (
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <input
                    placeholder="Comment (optional)"
                    value={comments[x.id] || ""}
                    onChange={e => setComment(x.id, e.target.value)}
                    className="bg-black border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-[#00ff90]/30 transition-colors w-44"
                  />
                  <button onClick={() => onApprove?.(x.id, comments[x.id] || "")}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                    Approve
                  </button>
                  <button onClick={() => onReject?.(x.id, comments[x.id] || "")}
                    className="text-xs px-3 py-1.5 rounded-lg border border-red-500/25 bg-red-500/8 text-red-400/70 hover:bg-red-500/15 hover:text-red-400 transition-colors">
                    Reject
                  </button>
                </div>
              ) : (
                <span className={`text-xs px-2.5 py-1 rounded-full border shrink-0 ${
                  x.status === "APPROVED" ? "border-[#00ff90]/20 text-[#00ff90]/50 bg-[#00ff90]/5"
                  : "border-red-500/20 text-red-400/50 bg-red-500/5"
                }`}>{x.status}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}